/* =========================================================
   RITO MINDERS — carrinho (anotação PT)
   - Adiciona/altera itens usando o MESMO fluxo do Horizon
     (evento CartLinesUpdateEvent + HTML das seções do carrinho),
     então o carrinho deslizante, o contador e o total se atualizam
     sem quebrar as funções nativas (quantidade, remover, cupom, nota).
   - Usado pela página de produto (rm-main-product) e pelo funil do
     carrinho (snippet rm-cart-funnel).
   ========================================================= */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const sectionIds = () => {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
};

const getCart = async () => {
  const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
};

const openDrawer = () => {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  else if (!drawer && !location.pathname.endsWith('/cart')) location.href = `${root()}cart`;
};

/**
 * Adiciona itens ao carrinho.
 * @param {Array<{id:number|string, quantity:number, properties?:object}>} items
 * @param {Element} [source] elemento que disparou (fica como alvo do evento)
 * @param {{open?: boolean}} [opts]
 */
export async function rmCartAdd(items, source = document.body, opts = {}) {
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 })),
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: ids.join(','), sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      const cart = await getCart();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'rm-cart' } });
      source.dispatchEvent(new CartErrorEvent({ error: data.description || data.message || 'Add to cart failed', code: 'INVALID' }));
      throw new Error(data.description || data.message || 'カートに追加できませんでした。');
    }
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, source: 'rm-cart', didError: false },
    });
    if (opts.open !== false) requestAnimationFrame(openDrawer);
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

/**
 * Altera a quantidade de uma linha (0 = remover).
 * @param {string} key chave da linha (line_item.key)
 * @param {number} quantity
 * @param {Element} [source]
 */
export async function rmCartChange(key, quantity, source = document.body) {
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: key, quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const res = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity, sections: ids.join(','), sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'error');
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { items: data.items, itemCount: data.item_count, sections: data.sections, source: 'rm-cart', didError: false },
    });
    return data;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

window.RMCart = { add: rmCartAdd, change: rmCartChange, open: openDrawer };

/* ---------- Funil do carrinho: botões e caixinhas (delegação de eventos) ---------- */
if (!window.__rmCartFunnelBound) {
  window.__rmCartFunnelBound = true;

  document.addEventListener('click', async (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-rm-add]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.hasAttribute('aria-busy')) return;
    const card = btn.closest('[data-rm-offer]');
    const select = card ? card.querySelector('[data-rm-variant]') : null;
    const id = (select && select.value) || btn.dataset.rmAdd;
    btn.setAttribute('aria-busy', 'true');
    try {
      await rmCartAdd([{ id: Number(id), quantity: 1 }], btn, { open: false });
    } catch (err) {
      console.warn('[rm-cart]', err);
      btn.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('change', async (e) => {
    const box = e.target instanceof HTMLInputElement && e.target.matches('[data-rm-toggle]') ? e.target : null;
    if (!box) return;
    const wrap = box.closest('[data-rm-offer]');
    wrap && wrap.setAttribute('aria-busy', 'true');
    try {
      if (box.checked) {
        await rmCartAdd([{ id: Number(box.dataset.rmToggle), quantity: 1 }], box, { open: false });
      } else if (box.dataset.rmKey) {
        await rmCartChange(box.dataset.rmKey, 0, box);
      }
    } catch (err) {
      console.warn('[rm-cart]', err);
      box.checked = !box.checked;
      wrap && wrap.removeAttribute('aria-busy');
    }
  });
}
